import { useState } from "react";
import { motion } from "framer-motion";
import { X, Check, Loader2, ShieldCheck } from "lucide-react";
import { conciergeBook } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable booking modal for demo sites (hotel, salon, auto, etc.).
 * props: demo, title, accent, submitLabel, selects [{key,label,options}], onClose, prefill
 */
export default function DemoBookingModal({ demo, title, accent = "#333", submitLabel = "Request appointment", selects = [], onClose, prefill = {} }) {
  const initSel = {};
  selects.forEach((s) => {
    const pre = prefill[s.key];
    initSel[s.key] = pre && s.options.includes(pre) ? pre : s.options[0];
  });
  const [sel, setSel] = useState(initSel);
  const [form, setForm] = useState({ date: "", time: "", name: "", contact: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!form.name.trim() || !form.contact.trim() || !form.date || !form.time) {
      setErr("Please fill in your name, contact, date and time."); return;
    }
    const isEmail = form.contact.includes("@");
    const service = selects.map((s) => sel[s.key]).filter(Boolean).join(" · ") || undefined;
    setBusy(true);
    try {
      const res = await conciergeBook({
        demo, session_id: `${demo}-web-${Date.now()}`, name: form.name.trim(),
        email: isEmail ? form.contact.trim() : null, phone: isEmail ? null : form.contact.trim(),
        date: form.date, time: form.time, service, notes: form.notes.trim() || null,
      });
      setDone(res.confirmation);
      toast.success("Request sent");
    } catch (e) { setErr(e?.response?.data?.detail || "Something went wrong."); }
    finally { setBusy(false); }
  };

  const field = "w-full rounded-lg px-3 py-2.5 text-sm outline-none border bg-white";
  const bd = { borderColor: "#e2e2e2" };
  return (
    <div className="fixed inset-0 z-[80] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid={`${demo}-booking-modal`}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="px-6 py-4 flex items-center justify-between text-white" style={{ background: accent }}>
          <div className="text-lg font-semibold">{title}</div>
          <button onClick={onClose} data-testid={`${demo}-booking-close`}><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-8 text-center" data-testid={`${demo}-booking-success`}>
            <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}><Check size={22} /></div>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{done}</p>
            <button onClick={onClose} className="mt-6 rounded-lg px-6 py-2.5 text-sm font-semibold text-white" style={{ background: accent }}>Done</button>
          </div>
        ) : (
          <div className="p-6 space-y-3">
            {selects.map((s) => (
              <div key={s.key}>
                <label className="text-[11px] uppercase tracking-widest text-slate-400">{s.label}</label>
                <select value={sel[s.key]} onChange={(e) => setSel({ ...sel, [s.key]: e.target.value })} className={`${field} mt-1`} style={bd} data-testid={`${demo}-booking-${s.key}`}>
                  {s.options.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div className="flex gap-3">
              <input type="date" min={new Date().toISOString().slice(0, 10)} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${field} w-1/2`} style={bd} data-testid={`${demo}-booking-date`} />
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={`${field} w-1/2`} style={bd} data-testid={`${demo}-booking-time`} />
            </div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={field} style={bd} data-testid={`${demo}-booking-name`} />
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or phone" className={field} style={bd} data-testid={`${demo}-booking-contact`} />
            {err && <div className="text-xs text-red-500" data-testid={`${demo}-booking-error`}>{err}</div>}
            <button onClick={submit} disabled={busy} data-testid={`${demo}-booking-submit`}
              className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: accent }}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} {submitLabel}
            </button>
            <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1"><ShieldCheck size={12} /> Secure &amp; private</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
